import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { useAppSelector } from "../../app/hooks";
import { isoToLocale, timeAgo } from "../../app/datetimeUtils";
import { draftTitle } from "../../app/draftHelper";
import Avatar from "../../components/Avatar";
import NoDataTableBody from "../../components/NoDataTableBody";
import RtkqResultViewer from "../../components/RtkqResultViewer";
import Select from "../../components/input/Select";
import { SelectDevelopmentView } from "../../components/input/SelectDevelopmentView";
import { SimpleOption } from "../../components/input/SimpleListbox";
import {
	PeerReviewListItemStatus,
	PeerReviewListItemViewModel,
	useGetAssignedReviewsQuery,
	useGetRequestedReviewsQuery,
} from "./peerReviewsApiSlice";
import { PeerReviewActionButton } from "./PeerReviewActionButton";

export default function StudentPeerReviews() {
	const [view, setView] = useState<string>("table");
	const [assignedOption, setAssignedOption] = useState("pending");
	const [requestedOption, setRequestedOption] = useState("complete");
	const [assignedQueryArg, setAssignedQueryArg] = useState<boolean | undefined>(false);
	const [requestedQueryArg, setRequestedQueryArg] = useState<boolean | undefined>(false);

	const assignedReviewsResult = useGetAssignedReviewsQuery(assignedQueryArg);
	const assignedReviewsData = assignedReviewsResult.data;

	const requestedReviewsResult = useGetRequestedReviewsQuery(requestedQueryArg);
	const requestedReviewsData = requestedReviewsResult.data;

	const location = useLocation();
	const navigate = useNavigate();

	const peerReviewOptions: SimpleOption[] = [
		{ value: "all", label: "All" },
		{ value: "pending", label: "Pending" },
		{ value: "complete", label: "Completed" },
	];

	const handleAssignedOptionsChange = (value: string) => {
		setAssignedOption(value);
	};

	const handleRequestedOptionsChange = (value: string) => {
		setRequestedOption(value);
	};

	useEffect(() => {
		if (location.state != null) {
			setAssignedOption(location.state.assignedFilter);
		}
		navigate(location, { replace: true });
	}, []);

	useEffect(() => {
		const q =
			assignedOption === "all" ? undefined : assignedOption === "pending" ? false : true;
		setAssignedQueryArg(q);
	}, [assignedOption]);

	useEffect(() => {
		const q =
			requestedOption === "all" ? undefined : requestedOption === "pending" ? false : true;
		setRequestedQueryArg(q);
	}, [requestedOption]);

	return (
		<section className="peerReview">
			<div className="flex justify-between">
				<div className="w-3/4">
					<h1>Peer Reviews</h1>
				</div>
				<SelectDevelopmentView
					value={view}
					setValue={(v) => setView(v as string)}
					simpleListBoxId="viewFilter"
					options={[
						{ value: "table", label: "Tables" },
						{ value: "raw", label: "Raw Data" },
					]}
					simpleListBoxClasses="print:hidden"
				/>
			</div>
			<p className="mt-4">
				Welcome to the Peer Review page! From this page, you can respond to peer review
				requests from your classmates and view the responses to your requests for peer
				review.
			</p>

			{view === "table" && (
				<>
					<div className="pb-8 mt-8">
						<h2>Reviews Requested By Your Peers</h2>

						<div className="mt-2 w-64">
							<Select
								value={assignedOption}
								setValue={(v) => handleAssignedOptionsChange(v as string)}
								label="Status Filter"
								simpleListBoxId="assignedOption"
								options={peerReviewOptions}
								simpleListBoxClasses="w-64"
							/>
						</div>

						<PeerReviewsTable
							filter={assignedOption}
							reviewCount={assignedReviewsData?.reviewCount}
							reviews={assignedReviewsData?.reviews}
							showAuthor={true}
							emptyMessages={{
								pending: `Congratulations! You have finished all the reviews your peers requested from you!`,
								complete: `It looks like you haven’t completed any peer reviews!`,
								all: `No peer reviews have been requested for you to complete.`,
							}}
						/>
					</div>
					<div>
						<h2>Reviews You Requested From Your Peers </h2>

						<div className="mt-2">
							<Select
								value={requestedOption}
								setValue={(v) => handleRequestedOptionsChange(v as string)}
								label="Status Filter"
								simpleListBoxId="requestedOption"
								options={peerReviewOptions}
								simpleListBoxClasses="w-64"
							/>
						</div>

						<PeerReviewsTable
							filter={requestedOption}
							reviewCount={requestedReviewsData?.reviewCount}
							reviews={requestedReviewsData?.reviews}
							showReviewer={true}
							emptyMessages={{
								pending: `Your peers have completed all the reviews you requested from them.`,
								complete: `It looks like your peers haven’t finished their reviews!`,
								all: `You have not requested any peer reviews.`,
							}}
						/>
					</div>
				</>
			)}
			{view === "raw" && (
				<>
					<RtkqResultViewer heading="Assigned Reviews" result={assignedReviewsResult} />
					<RtkqResultViewer heading="Requested Reviews" result={requestedReviewsResult} />
				</>
			)}
		</section>
	);
}

interface PeerReviewTableProps {
	filter: string;
	reviewCount?: number;
	reviews?: PeerReviewListItemViewModel[];
	showAuthor?: boolean;
	showReviewer?: boolean;
	emptyMessages: {
		pending: string;
		complete: string;
		all: string;
	};
}

function PeerReviewsTable(props: PeerReviewTableProps) {
	return (
		<table className="table-auto tbl tbl-data mt-2">
			<thead>
				<tr className="">
					<th className="hidden">ID</th>
					<th>Status</th>
					<th>When Requested</th>
					<th>When Reviewed</th>
					<th>Prompt/Title</th>
					{props.showAuthor && <th>Author</th>}
					{props.showReviewer && <th>Reviewer</th>}
					<th>Action</th>
				</tr>
			</thead>

			{!props.reviews ? (
				<tbody>
					<tr className="loading">
						<td colSpan={999}>Data loading...</td>
					</tr>
				</tbody>
			) : props.reviews.length === 0 ? (
				<NoDataTableBody columnCount={8} message={noDataMessage(props)} />
			) : (
				<tbody>
					{props.reviews.map((row, index) => {
						const canSeeReview =
							props.showAuthor ||
							(props.showReviewer &&
								row.status === PeerReviewListItemStatus.Complete);
						return (
							<tr key={row.reviewId}>
								<td className="hidden">{row.reviewId}</td>
								<td>{formatPeerReviewStatus(row.status)}</td>
								<td>
									<span data-tooltip-id={`request-date-tooltip-${row.reviewId}`}>
										{row.requestDate ? timeAgo(row.requestDate) : ""}
									</span>
									{row.requestDate && (
										<Tooltip
											id={`request-date-tooltip-${row.reviewId}`}
											place="top-start"
											content={isoToLocale(row.requestDate)}
										/>
									)}
								</td>
								<td>
									<span data-tooltip-id={`review-date-tooltip-${row.reviewId}`}>
										{row.reviewDate ? timeAgo(row.reviewDate) : ""}
									</span>
									{row.reviewDate && (
										<Tooltip
											id={`review-date-tooltip-${row.reviewId}`}
											place="top-start"
											content={isoToLocale(row.reviewDate)}
										/>
									)}
								</td>
								<td>
									{row.promptName}
									<br />
									<i>{draftTitle(row.draftTitle)}</i>
								</td>
								{props.showAuthor && (
									<td>
										<div className="flex space-x-4 items-center">
											<Avatar
												hasImage={row.author.hasImage}
												initials={row.author.initials ?? "?"}
												userId={row.author.userId}
											/>
											<div>{row.author.name}</div>
										</div>
									</td>
								)}
								{props.showReviewer && (
									<td>
										<div className="flex space-x-4 items-center">
											<Avatar
												hasImage={row.reviewer.hasImage}
												initials={row.reviewer.initials ?? "?"}
												userId={row.reviewer.userId}
											/>
											<div>{row.reviewer.name}</div>
										</div>
									</td>
								)}
								<td>
									{canSeeReview && (
										<PeerReviewActionButton
											action={row.status}
											reviewId={row.reviewId}></PeerReviewActionButton>
									)}
								</td>
							</tr>
						);
					})}
				</tbody>
			)}
		</table>
	);
}

const formatPeerReviewStatus = (status: PeerReviewListItemStatus): string => {
	switch (status) {
		case PeerReviewListItemStatus.Complete:
			return "Complete";
		case PeerReviewListItemStatus.InProgress:
			return "In Progress";
		case PeerReviewListItemStatus.NotStarted:
			return "Not Started";
	}
	return "Unknown";
};

const noDataMessage = (props: PeerReviewTableProps): string => {
	if (props.reviewCount === 0) return props.emptyMessages.all;
	else if (props.filter === "pending") return props.emptyMessages.pending;
	else if (props.filter === "complete") return props.emptyMessages.complete;

	return props.emptyMessages.all;
};
